import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import {
  eInvoiceCancelAction,
  eInvoiceCancelReasonsAction,
} from "../../../redux/actions/sales-action";
import errorToast from "../../helper-components/error-toast";
import successToast from "../../helper-components/success-toast";
import CancelDocumentModal from "../cancel-document-modal";
import { parseIrpError } from "./create-e-invoice";
import { getRowId } from "./e-invoice-utils";

/* NIC IRN cancellation reason codes – used only if the cancel-reasons API returns nothing */
const DEFAULT_CANCEL_REASONS = [
  { value: "1", label: "Duplicate" },
  { value: "2", label: "Data entry mistake" },
  { value: "3", label: "Order cancelled" },
  { value: "4", label: "Others" },
];

/* Rows of the cancel-reasons API (array directly, or under data / list / rows) */
const getReasonRows = (res) => {
  const d = res?.data ?? res;
  const rows = Array.isArray(d) ? d : d?.list ?? d?.rows ?? d?.data ?? d?.items ?? [];
  return Array.isArray(rows) ? rows : [];
};

/* { value: NIC reason code (CnlRsn), label } */
const toReasonOption = (r) => ({
  value: String(r?.code ?? r?.reason_code ?? r?.value ?? r?.id ?? ""),
  label: r?.name ?? r?.reason ?? r?.label ?? r?.description ?? String(r?.code ?? ""),
});

/* NIC limit for CnlRem */
const REMARK_MAX_LENGTH = 100;

/* Popup to cancel the IRN of an e-Invoice list row */
const CancelEInvoiceModal = ({ row, onClose, onCancelled }) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [reasonsLoading, setReasonsLoading] = useState(true);

  useEffect(() => {
    dispatch(
      eInvoiceCancelReasonsAction(
        { page: 1, limit: 100 },
        (res) => {
          const options = getReasonRows(res).map(toReasonOption).filter((o) => o.value);
          setReasons(options.length ? options : DEFAULT_CANCEL_REASONS);
          setReasonsLoading(false);
        },
        () => {
          setReasons(DEFAULT_CANCEL_REASONS);
          setReasonsLoading(false);
        }
      )
    );
  }, []);

  const inv = row?.sales_order ?? row;
  const status = row?.eInvoiceStatus ?? {};
  const irn = status?.irn ?? row?.irnDetails?.irn ?? "";
  const deadline = status?.cancellation_deadline;

  const handleConfirm = ({ reason, remark }) => {
    if (!irn) return;
    setSaving(true);
    setError(null);
    dispatch(
      eInvoiceCancelAction(
        {
          Irn: irn,
          CnlRsn: reason,
          CnlRem: remark,
          soit_id: getRowId(row),
        },
        (res) => {
          setSaving(false);
          successToast(res?.message ?? "e-Invoice cancelled successfully");
          onCancelled?.();
        },
        (err) => {
          setSaving(false);
          const parsed = parseIrpError(err);
          setError(parsed);
          errorToast(parsed.details[0] ?? parsed.message ?? "Failed to cancel e-Invoice");
        }
      )
    );
  };

  return (
    <CancelDocumentModal
      title="Cancel e-Invoice"
      infoLines={[
        { label: "Transaction #", value: inv?.soit_invoice_number },
        { label: "IRN", value: irn },
      ]}
      note={
        deadline && moment(deadline).isValid()
          ? `Can be cancelled until ${moment(deadline).format("MMM D, YYYY, hh:mm A")}`
          : null
      }
      reasons={reasons}
      reasonsLoading={reasonsLoading}
      remarkMaxLength={REMARK_MAX_LENGTH}
      saving={saving}
      error={error}
      onClose={onClose}
      onConfirm={handleConfirm}
    />
  );
};

export default CancelEInvoiceModal;
